import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';

import { useToggleState } from '@garnish/core';
import { useNoticeBannersStackActions } from '@garnish/overlays';
import { ProductLineItemCard } from '@garnish/product';

import {
  useLineItemDescription,
  useLineItemIngredients,
  useLineItemName,
  useLineItemPrice,
} from '@order/shared-hooks';
import { checkIfCanBeReorderedByCustomer } from '@order/utils';

import { ReorderConflictModal } from '../../../../components';
import { type PartialLineItem, type PartialProduct } from '../../../../graphql';
import { type ReorderOrder, useReorderOrderLineItem } from '../../../../hooks';

export const OrderStatusLineItemCard = (props: OrderStatusLineItemCardProps) => {
  const {
    order,
    lineItem,
    isDisabled,
    onPress,
    onRemove,
    onQuantityChange,
    onFavorite,
    ...restProps
  } = props;
  const { id = '', quantity = 0, product } = lineItem;

  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackActions();

  const lineItemName = useLineItemName(product, lineItem);
  const lineItemA11yLabel = formatMessage(messages.lineItemA11yLabel, { name: lineItemName }); // prettier-ignore
  const lineItemPrice = useLineItemPrice(lineItem);
  const lineItemDescription = useLineItemDescription(lineItem);
  const { addedIngredients, removedIngredients } = useLineItemIngredients(lineItem);

  const lineItemAssetUrl = product?.asset?.url ?? '';
  const canReorderLineItem = checkIfCanBeReorderedByCustomer(lineItem);

  // ─── State ───────────────────────────────────────────────────────────

  const {
    value: isConflictReviewModalVisible,
    toggleOn: showConflictReviewModal,
    toggleOff: hideConflictReviewModal,
  } = useToggleState(false);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onSuccessfulReorder = useCallback(() => {
    hideConflictReviewModal();

    addNoticeBanner({
      palette: 'success',
      autoHideTimeout: 2500,
      text: formatMessage(messages.successfulReorderMessage, {
        product: lineItem.product.name,
      }),
    });
  }, [addNoticeBanner, formatMessage, lineItem.product.name, hideConflictReviewModal]);

  const {
    reorderLineItem,
    isFetching: isReordering,
    unavailableIngredients,
    targetLocation,
  } = useReorderOrderLineItem({
    variation: 'order',
    mostRecentOrder: order,
    productId: lineItem.product.id,
    productCost: lineItem.product.cost,
    productName: lineItem.product.name,
    onUnavailableIngredientConflict: showConflictReviewModal,
    onSuccess: onSuccessfulReorder,
  });

  const reorderLineItemForce = useCallback(async () => {
    await reorderLineItem(id, true);
  }, [id, reorderLineItem]);

  const handleOnPress = useCallback(() => {
    onPress?.(id, product);
  }, [onPress, product, id]);

  const handleOnRemove = useCallback(() => {
    onRemove?.(id, product);
  }, [onRemove, product, id]);

  const handleQuantityChange = useCallback(
    async (updatedQuantity: number) => {
      await onQuantityChange?.(lineItem, updatedQuantity);
    },
    [lineItem, onQuantityChange],
  );

  const handleOnFavorite = useCallback(() => {
    void onFavorite?.({
      lineItemId: id,
      productId: product.id,
      favorited: !lineItem.favorited,
    });
  }, [onFavorite, id, product.id, lineItem.favorited]);

  // ────────────────────────────────────────────────────────────────────

  return (
    <>
      <ProductLineItemCard
        productId={id}
        productName={lineItemName}
        price={lineItemPrice}
        cloudinaryImageUrl={lineItemAssetUrl}
        imageSize="large"
        addedIngredients={addedIngredients}
        addedIngredientsLabel={formatMessage(messages.productAddedIngredientsLabel)} // prettier-ignore
        removedIngredients={removedIngredients}
        removedIngredientsLabel={formatMessage(messages.productRemovedIngredientsLabel)} // prettier-ignore
        description={lineItemDescription}
        quantity={quantity}
        onPress={onPress ? handleOnPress : undefined}
        onRemove={onRemove ? handleOnRemove : undefined}
        onFavorite={onFavorite ? handleOnFavorite : undefined}
        onQuantityChange={onQuantityChange ? handleQuantityChange : undefined}
        isDisabled={isDisabled}
        removeBtnLabel={formatMessage(messages.removeBtnLabel)}
        accessibilityLabel={lineItemA11yLabel}
        isAddingToBag={isReordering}
        onAddToBag={canReorderLineItem ? reorderLineItem : undefined}
        {...restProps}
      />

      <ReorderConflictModal
        visible={isConflictReviewModalVisible}
        lineItem={lineItem as any}
        isReordering={isReordering}
        onRequestClose={hideConflictReviewModal}
        addToBagAnyway={reorderLineItemForce}
        targetLocation={targetLocation}
        unavailableIngredients={unavailableIngredients}
      />
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly removeBtnLabel: NoMessageValues;
  readonly successfulReorderMessage: { readonly product: MessageValue };
  readonly productAddedIngredientsLabel: NoMessageValues;
  readonly productRemovedIngredientsLabel: NoMessageValues;
  readonly lineItemA11yLabel: { readonly name: MessageValue };
}>({
  removeBtnLabel: {
    defaultMessage: 'Remove',
    description: 'Order Status | Line Item Card | Remove button label',
  },
  successfulReorderMessage: {
    defaultMessage: '1x {product} added to your bag!',
    description: 'Order Status | Line Item Card | Successful reorder message',
  },
  productAddedIngredientsLabel: {
    defaultMessage: 'Added:',
    description: 'Order Status | Line Item Card | Product added ingredients label',
  },
  productRemovedIngredientsLabel: {
    defaultMessage: 'Removed:',
    description: 'Order Status | Line Item Card | Product removed ingredients label',
  },
  lineItemA11yLabel: {
    defaultMessage: 'Navigate to {name} details page',
    description: 'Order Status | Line Item Card | Line item a11y label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ToggleFavoriteType = Readonly<{
  lineItemId?: string;
  productId?: string;
  favorited: boolean;
}>;

type OrderStatusLineItemCardProps = Readonly<{
  order: ReorderOrder;
  lineItem: LineItem;
  isDisabled?: boolean;
  onPress?: (lineItemId: string, product: PartialProduct) => void;
  onRemove?: (lineItemId: string, product: PartialProduct) => void;
  onQuantityChange?: (lineItem: LineItem, quantity: number) => Promise<unknown>;
  onFavorite?: (params: ToggleFavoriteType) => Promise<void>;
}> &
  Partial<
    Pick<
      ComponentProps<typeof ProductLineItemCard>,
      | 'isFetchingFavorite'
      | 'isFavorite'
      | 'onShare'
      | 'shareBtnLabel'
      | 'shareTooltipLabel'
      | 'shareUrl'
    >
  >;

type LineItem = Omit<PartialLineItem, 'ingredients'>;
